import React, { useState, useEffect, useMemo } from 'react';
import { Bell, Clock, CheckCircle, Repeat, Calendar, Pill, Zap } from 'lucide-react';
import Navigation from '../components/Navigation';
import apiClient from '../utils/api';
import { handleApiError } from '../utils/errorHandler';
import { useTranslation } from '../i18n';
import { format, isAfter, isBefore, addHours, isValid, parseISO } from 'date-fns';

const Reminders = () => {
  const { t } = useTranslation();
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('scheduled');

  useEffect(() => {
    fetchReminders();
  }, []);

  const fetchReminders = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/reminders');
      setReminders(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      setError(handleApiError(err, 'Failed to load reminders', t));
      console.error('Error fetching reminders:', err);
    } finally {
      setLoading(false);
    }
  };

  const parseDate = (dateString) => {
    if (!dateString) return null;
    try {
      const date = parseISO(dateString);
      return isValid(date) ? date : null;
    } catch {
      return null;
    }
  };

  const now = useMemo(() => new Date(), []);
  const next24Hours = useMemo(() => addHours(now, 24), [now]);
  const safeReminders = useMemo(() => Array.isArray(reminders) ? reminders : [], [reminders]);

  const filterCounts = useMemo(() => {
    const counts = { scheduled: 0, upcoming: 0, overdue: 0, cancelled: 0, recurring: 0 };
    
    safeReminders.forEach(reminder => {
      const scheduledDate = parseDate(reminder.scheduled_time);
      const isRecurring = reminder.recurrence_type && reminder.recurrence_type !== 'once';
      
      if (isRecurring) {
        if (reminder.status === 'active' || reminder.status === 'scheduled') {
          counts.recurring++;
        }
        return; // Recurring reminders only appear in the routines tab
      }
      
      if (reminder.status === 'scheduled') {
        counts.scheduled++;
        if (scheduledDate) {
          if (isBefore(scheduledDate, now)) {
            counts.overdue++;
          } else if (isBefore(scheduledDate, next24Hours)) {
            counts.upcoming++;
          }
        }
      } else if (reminder.status === 'cancelled') {
        counts.cancelled++;
      }
    });
    
    return counts;
  }, [safeReminders, now, next24Hours]);

  const filteredReminders = useMemo(() => {
    switch (filter) {
      case 'upcoming':
        return safeReminders.filter(reminder => {
          const scheduledDate = parseDate(reminder.scheduled_time);
          const isOneTime = !reminder.recurrence_type || reminder.recurrence_type === 'once';
          return isOneTime && reminder.status === 'scheduled' && 
                 scheduledDate && 
                 isAfter(scheduledDate, now) &&
                 isBefore(scheduledDate, next24Hours);
        });
      case 'overdue':
        return safeReminders.filter(reminder => {
          const scheduledDate = parseDate(reminder.scheduled_time);
          const isOneTime = !reminder.recurrence_type || reminder.recurrence_type === 'once';
          return isOneTime && reminder.status === 'scheduled' && 
                 scheduledDate && 
                 isBefore(scheduledDate, now);
        });
      case 'recurring':
        return safeReminders.filter(reminder => 
          reminder.recurrence_type && reminder.recurrence_type !== 'once' &&
          (reminder.status === 'active' || reminder.status === 'scheduled')
        );
      case 'cancelled':
        return safeReminders.filter(reminder => reminder.status === 'cancelled');
      case 'scheduled':
        return safeReminders.filter(reminder => {
          const isOneTime = !reminder.recurrence_type || reminder.recurrence_type === 'once';
          return isOneTime && reminder.status === 'scheduled';
        });
      default:
        return safeReminders;
    }
  }, [safeReminders, filter, now, next24Hours]);

  const getReminderStatus = (reminder) => {
    const scheduledDate = parseDate(reminder.scheduled_time);
    
    if (reminder.status === 'completed') {
      return { label: 'Completed', class: 'badge-success', icon: CheckCircle };
    }
    if (reminder.status === 'cancelled') {
      return { label: 'Cancelled', class: 'badge-secondary', icon: Bell };
    }
    if (!scheduledDate) {
      return { label: 'Invalid Date', class: 'badge-danger', icon: Bell };
    }
    if (isBefore(scheduledDate, now)) {
      return { label: 'Overdue', class: 'badge-danger', icon: Bell };
    }
    if (isBefore(scheduledDate, next24Hours)) {
      return { label: 'Due Soon', class: 'badge-warning', icon: Clock };
    }
    return { label: 'Scheduled', class: 'badge-primary', icon: Clock };
  };

  const formatDateTime = (dateString) => {
    const date = parseDate(dateString);
    if (date) {
      return format(date, 'MMM d, yyyy \'at\' h:mm a');
    }
    return dateString || 'Invalid date';
  };

  const formatTime = (dateString) => {
    const date = parseDate(dateString);
    if (date) {
      return format(date, 'h:mm a');
    }
    return '';
  };

  if (loading) {
    return (
      <>
        <Navigation />
        <div className="container">
          <div className="loading">
            <div className="spinner"></div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Navigation />
      <div className="container" style={{ paddingBottom: '5rem' }}>
        <div style={{ marginBottom: '1.5rem' }}>
          <h1 className="text-lg" style={{ fontWeight: '700', marginBottom: '0.5rem' }}>
            {t('reminders')}
          </h1>
          <p className="text-sm" style={{ color: '#666' }}>
            {t('upcomingReminders')}
          </p>
        </div>

        {error && <div className="error">{error}</div>}

        {/* Filter Tabs */}
        <div className="card">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0.5rem' }}>
            {[
              { key: 'scheduled', label: t('scheduled'), count: filterCounts.scheduled },
              { key: 'upcoming', label: t('next24h'), count: filterCounts.upcoming },
              { key: 'recurring', label: `🔁 ${t('routines')}`, count: filterCounts.recurring },
              { key: 'overdue', label: t('overdue'), count: filterCounts.overdue }
            ].map(({ key, label, count }) => (
              <button
                key={key}
                onClick={() => setFilter(key)}
                className={`btn ${filter === key ? 'btn-primary' : 'btn-outline'}`}
                style={{ 
                  fontSize: '0.8rem',
                  padding: '0.5rem 0.5rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                {label} ({count})
              </button>
            ))}
          </div>
        </div>

        {/* Recurring Reminders — Dedicated Experience */}
        {filter === 'recurring' ? (
          <RecurringView reminders={filteredReminders} formatTime={formatTime} />
        ) : (
          /* Standard Reminders List */
          <div className="card">
            <div className="card-header">
              <h2 className="card-title">
                {filter === 'scheduled' ? t('scheduledReminders') :
                 filter === 'upcoming' ? t('next24Hours') :
                 filter === 'overdue' ? t('overdueReminders') :
                 t('allReminders')} ({filteredReminders.length})
              </h2>
            </div>
            
            {filteredReminders.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
                <Bell size={48} style={{ marginBottom: '1rem', opacity: 0.5 }} />
                <p>{t('noReminders')}</p>
                <p style={{ fontSize: '0.875rem' }}>
                  {filter === 'scheduled' 
                    ? t('createRemindersBot')
                    : t('noRemindersFilter').replace('{filter}', filter)
                  }
                </p>
              </div>
            ) : (
              <ul className="list">
                {filteredReminders.map((reminder) => {
                  const status = getReminderStatus(reminder);
                  const StatusIcon = status.icon;
                  
                  return (
                    <li key={reminder.reminder_id} className="list-item">
                      <div className="list-item-content">
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                          <StatusIcon size={16} />
                          <div className="list-item-title">{reminder.title}</div>
                          <span className={`badge ${status.class}`}>{status.label}</span>
                        </div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', fontSize: '0.75rem', color: '#666' }}>
                          <span>
                            <Clock size={12} style={{ marginRight: '0.25rem' }} />
                            {formatDateTime(reminder.scheduled_time)}
                          </span>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        )}

        {/* Quick Stats */}
        {safeReminders.length > 0 && (
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-number">{filterCounts.overdue}</div>
              <div className="stat-label">{t('overdue')}</div>
            </div>
            <div className="stat-card">
              <div className="stat-number">{filterCounts.upcoming}</div>
              <div className="stat-label">{t('upcoming')}</div>
            </div>
            <div className="stat-card">
              <div className="stat-number">{filterCounts.recurring}</div>
              <div className="stat-label">{t('routines')}</div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};


/**
 * Dedicated view for recurring reminders — shows them as "routines" with
 * visual cadence info, time slots, and streak tracking.
 */
const RecurringView = ({ reminders, formatTime }) => {
  const { t } = useTranslation();
  
  // Group recurring reminders by message (same medication at different times = one routine)
  const routines = useMemo(() => {
    const grouped = {};
    
    reminders.forEach(r => {
      const key = r.title.toLowerCase().trim();
      if (!grouped[key]) {
        grouped[key] = {
          name: r.title,
          recurrence_type: r.recurrence_type,
          recurrence_rule: r.recurrence_rule,
          recurrence_end: r.recurrence_end,
          status: r.status,
          slots: [],
          total_occurrences: 0
        };
      }
      grouped[key].slots.push({
        reminder_id: r.reminder_id,
        time: formatTime(r.next_scheduled_time || r.scheduled_time),
        scheduled_time: r.scheduled_time,
        next_scheduled_time: r.next_scheduled_time,
        occurrence_count: r.occurrence_count || 0
      });
      grouped[key].total_occurrences += (r.occurrence_count || 0);
    });

    return Object.values(grouped).sort((a, b) => b.total_occurrences - a.total_occurrences);
  }, [reminders, formatTime]);

  const getRecurrenceIcon = (type) => {
    switch (type) {
      case 'hourly': return <Zap size={18} color="#f59e0b" />;
      case 'daily': return <Pill size={18} color="#8b5cf6" />;
      case 'monthly': return <Calendar size={18} color="#3b82f6" />;
      default: return <Repeat size={18} color="#6b7280" />;
    }
  };

  const getRecurrenceLabel = (type, rule) => {
    if (type === 'interval' && rule) {
      const interval = rule.interval || 1;
      const unit = rule.unit || 'hours';
      if (unit === 'minutes') return interval === 1 ? t('everyMinute') : t('everyNMinutes').replace('{n}', interval);
      if (unit === 'hours') return interval === 1 ? t('everyHour') : t('everyNHours').replace('{n}', interval);
      if (unit === 'days') return interval === 1 ? t('everyDay') : t('everyNDays').replace('{n}', interval);
    }
    if (type === 'hourly') {
      const interval = (rule && rule.interval) || 1;
      return interval === 1 ? t('everyHour') : t('everyNHours').replace('{n}', interval);
    }
    switch (type) {
      case 'daily': return t('everyDay');
      case 'monthly': return t('everyMonth');
      default: return t('recurring');
    }
  };

  if (routines.length === 0) {
    return (
      <div className="card" style={{ textAlign: 'center', padding: '2rem' }}>
        <Repeat size={48} style={{ marginBottom: '1rem', opacity: 0.4, color: '#8b5cf6' }} />
        <p style={{ fontWeight: '600', marginBottom: '0.5rem' }}>{t('noRoutines')}</p>
        <p style={{ fontSize: '0.875rem', color: '#666' }}>
          {t('noRoutinesDesc')}<br />
          <em>{t('noRoutinesExample')}</em>
        </p>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
      {routines.map((routine, idx) => (
        <div key={idx} className="card" style={{ padding: '1rem' }}>
          {/* Header */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem' }}>
            {getRecurrenceIcon(routine.recurrence_type)}
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: '600', fontSize: '0.95rem' }}>{routine.name}</div>
              <div style={{ fontSize: '0.75rem', color: '#888' }}>
                {getRecurrenceLabel(routine.recurrence_type, routine.recurrence_rule)}
                {routine.recurrence_end && ` · ${t('until')} ${routine.recurrence_end}`}
              </div>
            </div>
            <div style={{ 
              background: routine.status === 'active' || routine.status === 'scheduled' ? '#dcfce7' : '#f3f4f6',
              color: routine.status === 'active' || routine.status === 'scheduled' ? '#166534' : '#6b7280',
              padding: '0.25rem 0.6rem',
              borderRadius: '9999px',
              fontSize: '0.7rem',
              fontWeight: '600'
            }}>
              {routine.status === 'active' || routine.status === 'scheduled' ? t('activeStatus') : routine.status}
            </div>
          </div>

          {/* Time Slots */}
          {routine.slots.length > 0 && (
            <div style={{ 
              display: 'flex', 
              flexWrap: 'wrap', 
              gap: '0.5rem', 
              marginBottom: '0.75rem' 
            }}>
              {routine.slots
                .sort((a, b) => (a.time || '').localeCompare(b.time || ''))
                .map((slot, i) => (
                <div key={i} style={{
                  background: '#f8f9fa',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  padding: '0.4rem 0.75rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.4rem',
                  fontSize: '0.85rem'
                }}>
                  <Clock size={12} color="#6b7280" />
                  <span style={{ fontWeight: '500' }}>{slot.time || 'N/A'}</span>
                </div>
              ))}
            </div>
          )}

          {/* Stats */}
          <div style={{ 
            display: 'flex', 
            gap: '1.5rem', 
            fontSize: '0.75rem', 
            color: '#888',
            borderTop: '1px solid #f0f0f0',
            paddingTop: '0.6rem'
          }}>
            <span>
              🔥 {t('timesFired').replace('{count}', routine.total_occurrences)}
            </span>
            {routine.slots.length > 1 && (
              <span>⏰ {t('dailySlots').replace('{count}', routine.slots.length)}</span>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

export default Reminders;
